import { Route, Routes, useNavigate } from "react-router";
import type { OrgEntity } from "~/core/api";
import { useT } from "~/core/i18n";
import { Empty } from "~/core/ui/states";
import type { FilterState } from "~/modules/analytics/components/filters";
import { LOOP, readingPath } from "~/modules/analytics/lib/loop";
import { ComposeDialog } from "~/modules/analytics/pages/compose";
import { EditorPage } from "~/modules/analytics/pages/editor";
import { OverviewPage } from "~/modules/analytics/pages/overview";
import { PagesPage } from "~/modules/analytics/pages/pages";
import { StageRoutes } from "~/modules/analytics/pages/stages";

/**
 * The drafts, with the composer open over them.
 *
 * The list renders underneath rather than a blank page behind a box: closing
 * the composer is a navigation, and what it navigates back to should already be
 * on the screen when it goes.
 */
function ComposeRoute(props: {
	entityId: string;
	filters: FilterState;
	setFilters: (next: Partial<FilterState>) => void;
}) {
	const navigate = useNavigate();
	return (
		<>
			<PagesPage {...props} stage="draft" />
			<ComposeDialog {...props} onClose={() => navigate(readingPath(`/w/${props.entityId}`, "drafts"))} />
		</>
	);
}

/**
 * Analytics: the five stages of the loop, and the screens that sit outside the
 * numbers because they belong to all five.
 *
 * The module has no path prefix of its own. It is what a brand's workspace is
 * when you have not gone somewhere else, so it occupies the brand root and the
 * Agent takes one segment under it — which is also why stage one's own path,
 * `insights`, reads as the module's entry.
 */
export function AnalyticsRoutes({
	orgId,
	brand,
	filters,
	setFilters,
}: {
	orgId: string;
	brand: OrgEntity;
	filters: FilterState;
	setFilters: (next: Partial<FilterState>) => void;
}) {
	const t = useT();
	const shared = { entityId: brand.id, filters, setFilters };

	return (
		<div className="analytics-workspace">
			<Routes>
				<Route index element={<OverviewPage {...shared} orgId={orgId} />} />
				{/* One route per stage, from `LOOP`. `/*` because every reading is
			    somewhere the assistant can send a person, somewhere an empty state
			    can link to, and somewhere a bookmark still lands. */}
				{LOOP.map((stage) => (
					<Route key={stage.key} path={`${stage.path}/*`} element={<StageRoutes stage={stage} {...shared} />} />
				))}
				{/* The composer, over the list it adds to. Still a path of its own so
			    the assistant can open it and a bookmark still works; closing it
			    leaves the drafts underneath. */}
				<Route path="compose" element={<ComposeRoute {...shared} />} />
				{/* The editor is a screen, not a modal: it is where somebody reads a
			    page before putting their name on it, and that is not a thing to do
			    in a box over the list. It keeps its own path rather than moving
			    under the generate stage: a page is read from the drafts list and
			    from the published one and from a reflow report, and a path that
			    named one stage would be wrong from the other two. */}
				<Route path="pages/:pageId" element={<EditorPage {...shared} />} />
				<Route path="*" element={<Empty title={t("errors.not_found")} />} />
			</Routes>
		</div>
	);
}
